import React, { forwardRef } from "react";
import { formatDisplayDate } from "utils/dateFormat";
import { useInvoicePrintTheme } from "hooks/useInvoicePrintTheme";
import { getInvoicePrintRootClassName } from "utils/invoicePrintTheme";

const fmtMoney = (n) => {
  const num = Number(n || 0);
  return num.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtMoneyDollar = (n) => `$${fmtMoney(n)}`;

const fmtDate = (d) => formatDisplayDate(d);

const splitLines = (s) =>
  String(s || "")
    .split(/\r?\n|,/)
    .map((line) => line.trim())
    .filter(Boolean);

const maskAccount = (bsb, account) => {
  if (!bsb && !account) return "-";
  const acc = String(account || "");
  const tail = acc.slice(-4) || "----";
  const stars = "*".repeat(Math.max(acc.length - 4, 4));
  const bsbPart = bsb ? `(${bsb})` : "";
  return `${bsbPart}${stars}${tail}`;
};

const buildSuperLabel = (s) => {
  const parts = [];
  if (s?.description) {
    const d = s.description.includes("SGC")
      ? "SGC"
      : s.description.replace("Superannuation Guarantee (SGC)", "SGC");
    parts.push(d);
  }
  if (s?.fund) parts.push(s.fund);
  if (s?.memberNumber) parts.push(s.memberNumber);
  return parts.join(" - ");
};

const PayslipTemplate = forwardRef(({ data, darkPrint: darkPrintProp }, ref) => {
  const { darkPrint: darkPrintHook } = useInvoicePrintTheme();
  const darkPrint = darkPrintProp ?? darkPrintHook;
  const company = data?.company || {};
  const employee = data?.employee || {};
  const period = data?.period || {};
  const earnings = data?.earnings || [];
  const taxes = data?.taxes || [];
  const supers = data?.supers || [];
  const deductions = data?.deductions || [];
  const totals = data?.totals || {};

  const employeeAddressLines = splitLines(employee?.address);
  const companyAddressLines = splitLines(company?.address);

  return (
    <div className={`printContent ${getInvoicePrintRootClassName(darkPrint)}`} ref={ref}>
      <div className="payslip-box xero-payslip">
        {/* TOP ROW: employee on left, two info boxes on right */}
        <div className="xp-top">
          <div className="xp-employee">
            <div className="xp-name">{employee?.name || "Employee Name"}</div>
            {employeeAddressLines.length === 0 ? (
              <div className="xp-line muted">Address line</div>
            ) : (
              employeeAddressLines.map((line, i) => (
                <div key={`emp-addr-${i}`} className="xp-line">
                  {line}
                </div>
              ))
            )}
          </div>

          <div className="xp-info-stack">
            <div className="xp-info-box">
              <div className="xp-info-title">PAID BY</div>
              <div className="xp-info-line strong">{company?.companyName || "Company Name"}</div>
              {companyAddressLines.map((line, i) => (
                <div key={`co-addr-${i}`} className="xp-info-line">
                  {line}
                </div>
              ))}
              {company?.abn && <div className="xp-info-line">ABN {company.abn}</div>}
            </div>

            <div className="xp-info-box">
              <div className="xp-info-title">EMPLOYMENT DETAILS</div>
              {period?.frequency && (
                <div className="xp-info-line">Pay Frequency: {period.frequency}</div>
              )}
              {employee?.classification && (
                <div className="xp-info-line">Classification: {employee.classification}</div>
              )}
              {employee?.position && (
                <div className="xp-info-line">Position: {employee.position}</div>
              )}
            </div>
          </div>
        </div>

        {/* SUMMARY BAR */}
        <div className="xp-summary-bar px-1">
          <div>
            Pay Period:{" "}
            <strong>
              {fmtDate(period?.start)} - {fmtDate(period?.end)}
            </strong>
          </div>
          <div>
            Payment Date: <strong>{fmtDate(period?.paymentDate)}</strong>
          </div>
          <div>
            Total Earnings: <strong>{fmtMoneyDollar(totals?.earnings)}</strong>
          </div>
          <div>
            Net Pay: <strong>{fmtMoneyDollar(totals?.netPay)}</strong>
          </div>
        </div>

        {/* THIS PAY / YTD column headers */}
        <div className="xp-col-header">
          <span className="xp-col-header-this">THIS PAY</span>
          <span className="xp-col-header-ytd">YTD</span>
        </div>

        {/* SALARY & WAGES */}
        <Section title="SALARY & WAGES">
          <table className="xp-table xp-table-earnings">
            <thead>
              <tr>
                <th className="xp-desc" />
                <th className="xp-hours" />
                <th className="xp-rate-label">RATE</th>
                <th className="xp-amount" />
                <th className="xp-ytd" />
              </tr>
            </thead>
            <tbody>
              {earnings.length === 0 ? (
                <tr>
                  <td className="xp-desc muted">No earnings</td>
                  <td />
                  <td />
                  <td />
                  <td />
                </tr>
              ) : (
                earnings.map((e, i) => (
                  <tr key={`er-${i}`}>
                    <td className="xp-desc">-- {e?.description || "Earning"}</td>
                    <td className="xp-hours">
                      {e?.hours
                        ? Number(e.hours)
                            .toFixed(4)
                            .replace(/\.?0+$/, "")
                        : ""}
                    </td>
                    <td className="xp-rate">{e?.rate ? `$${Number(e.rate).toFixed(4)}` : ""}</td>
                    <td className="xp-amount">{fmtMoneyDollar(e?.amount)}</td>
                    <td className="xp-ytd">{fmtMoneyDollar(e?.ytd)}</td>
                  </tr>
                ))
              )}
              {Number(totals?.earningsPriorYtd) > 0 && (
                <tr>
                  <td className="xp-desc">Other Previous Earnings</td>
                  <td />
                  <td />
                  <td />
                  <td className="xp-ytd">{fmtMoneyDollar(totals.earningsPriorYtd)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="xp-total-row">
                <td colSpan={3} className="xp-total-label">
                  TOTAL
                </td>
                <td className="xp-amount strong">{fmtMoneyDollar(totals?.earnings)}</td>
                <td className="xp-ytd strong">{fmtMoneyDollar(totals?.earningsYtdGrand)}</td>
              </tr>
            </tfoot>
          </table>
        </Section>

        {/* TAX */}
        <Section title="TAX">
          <table className="xp-table">
            <tbody>
              {(taxes.length === 0 ? [{ description: "PAYG", amount: 0, ytd: 0 }] : taxes).map(
                (t, i) => (
                  <tr key={`tx-${i}`}>
                    <td className="xp-desc">{t?.description || "PAYG"}</td>
                    <td className="xp-amount">{fmtMoneyDollar(t?.amount)}</td>
                    <td className="xp-ytd">{fmtMoneyDollar(t?.ytd)}</td>
                  </tr>
                )
              )}
            </tbody>
            <tfoot>
              <tr className="xp-total-row">
                <td className="xp-total-label">TOTAL</td>
                <td className="xp-amount strong">{fmtMoneyDollar(totals?.taxes)}</td>
                <td className="xp-ytd strong">{fmtMoneyDollar(totals?.taxesYtd)}</td>
              </tr>
            </tfoot>
          </table>
        </Section>

        {/* DEDUCTIONS - hidden if none */}
        {deductions.length > 0 && (
          <Section title="DEDUCTIONS">
            <table className="xp-table">
              <tbody>
                {deductions.map((d, i) => (
                  <tr key={`dd-${i}`}>
                    <td className="xp-desc">{d?.description || "-"}</td>
                    <td className="xp-amount">{fmtMoneyDollar(d?.amount)}</td>
                    <td className="xp-ytd">{fmtMoneyDollar(d?.ytd)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="xp-total-row">
                  <td className="xp-total-label">TOTAL</td>
                  <td className="xp-amount strong">{fmtMoneyDollar(totals?.deductions)}</td>
                  <td className="xp-ytd strong">{fmtMoneyDollar(totals?.deductionsYtd)}</td>
                </tr>
              </tfoot>
            </table>
          </Section>
        )}

        {/* SUPERANNUATION */}
        <Section title="SUPERANNUATION">
          <table className="xp-table">
            <tbody>
              {(supers.length === 0 ? [{ description: "SGC", amount: 0, ytd: 0 }] : supers).map(
                (s, i) => (
                  <tr key={`sp-${i}`}>
                    <td className="xp-desc">
                      {buildSuperLabel(s) || s?.description || "Superannuation"}
                    </td>
                    <td className="xp-amount">{fmtMoneyDollar(s?.amount)}</td>
                    <td className="xp-ytd">{fmtMoneyDollar(s?.ytd)}</td>
                  </tr>
                )
              )}
            </tbody>
            <tfoot>
              <tr className="xp-total-row">
                <td className="xp-total-label">TOTAL</td>
                <td className="xp-amount strong">{fmtMoneyDollar(totals?.supers)}</td>
                <td className="xp-ytd strong">{fmtMoneyDollar(totals?.supersYtd)}</td>
              </tr>
            </tfoot>
          </table>
        </Section>

        {/* PAYMENT DETAILS */}
        <div className="xp-section xp-payment">
          <div className="xp-section-title-row">
            <span className="xp-section-title">PAYMENT DETAILS</span>
            <span className="xp-pd-headers">
              <span className="xp-pd-ref">REFERENCE</span>
              <span className="xp-pd-amt">AMOUNT</span>
            </span>
          </div>
          <table className="xp-table xp-table-payment">
            <tbody>
              <tr>
                <td className="xp-pd-account">
                  {maskAccount(employee?.bsb, employee?.accountNumber)}
                </td>
                <td className="xp-pd-name">{employee?.name || "-"}</td>
                <td className="xp-pd-ref-cell">Wages</td>
                <td className="xp-pd-amt-cell">{fmtMoneyDollar(totals?.netPay)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {data?.notes && <div className="xp-notes">{data.notes}</div>}
      </div>
    </div>
  );
});

const Section = ({ title, children }) => (
  <div className="xp-section">
    <div className="xp-section-title">{title}</div>
    {children}
  </div>
);

PayslipTemplate.displayName = "PayslipTemplate";

export default PayslipTemplate;
